import { useCallback, useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, X, Coins, Pause, Play, CalendarClock, Repeat, PlusCircle, ClipboardCheck, Clock, Hand } from 'lucide-react'
import {
  formatPoints,
  pointsToEuro,
  TASK_RECURRENCES,
  type MemberDto,
  type TaskTemplateDto,
  type PointsAccountDto,
  type TaskRecurrence,
  type ListItemDto,
} from '@familynest/shared'
import { api } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { useLive } from '../../lib/ws'
import { Avatar, Button, useBackdropClose } from '../ui'
import { useDialogs } from '../ui/dialogs'

const field = 'w-full rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]'
const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const
const WD = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const RECUR_LABEL: Record<TaskRecurrence, string> = { once: 'Einmalig', daily: 'Täglich', weekly: 'Wöchentlich', monthly: 'Monatlich' }

const sortWd = (a: number, b: number): number => (a === 0 ? 7 : a) - (b === 0 ? 7 : b) // Mo…So
function recurrenceText(t: TaskTemplateDto): string {
  // Aufgaben haben keine Uhrzeit mehr (ADR-070) → nur noch Rhythmus / Datum.
  if (t.recurrence === 'weekly') {
    if (t.daysOfWeek.length >= 7) return 'Täglich'
    return `Wöchentlich · ${t.daysOfWeek.slice().sort(sortWd).map((i) => WD[i]).join(', ')}`
  }
  if (t.recurrence === 'monthly') {
    if (t.daysOfMonth.length >= 5) return `Monatlich · ${t.daysOfMonth.length} Tage`
    return `Monatlich · ${t.daysOfMonth.slice().sort((a, b) => a - b).map((d) => `${d}.`).join(', ')}`
  }
  if (t.recurrence === 'daily') return 'Täglich'
  return t.dueAt ? `Einmalig · bis ${new Date(t.dueAt).toLocaleDateString('de-DE')}` : 'Einmalig'
}

/**
 * „Aufgaben" (ADR-039). Eltern verwalten Aufgaben-Vorlagen und sehen die
 * Punktekonten aller Kinder (mit Auszahlung ab 1.000 P). Kinder sehen ihr
 * eigenes Punktekonto (Fortschritt Richtung Auszahlung) und die geplanten
 * Aufgaben. Die eigentliche Erledigung passiert in „Zu erledigen".
 */
export function TasksScreen({ members }: { members: MemberDto[] }): React.JSX.Element {
  const { member } = useAuth()
  const dialogs = useDialogs()
  const isAdult = member?.role === 'adult'
  const [templates, setTemplates] = useState<TaskTemplateDto[]>([])
  const [accounts, setAccounts] = useState<PointsAccountDto[]>([])
  const [todoItems, setTodoItems] = useState<ListItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog] = useState<{ editing: TaskTemplateDto | null } | null>(null)
  const [creditOpen, setCreditOpen] = useState(false)
  const children = members.filter((m) => m.role === 'child')
  const nameOf = (id: string | null): string => (id ? (members.find((m) => m.id === id)?.name ?? 'Kind') : 'Alle Kinder')

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([api.listTaskTemplates(), api.listPointsAccounts()])
      setTemplates(t)
      setAccounts(a)
      // Kinder: offene Aufgaben aus der „Zu erledigen"-Liste direkt hier zeigen.
      if (member?.role === 'child') {
        const lists = await api.listLists()
        const todo = lists.find((l) => l.type === 'todo')
        setTodoItems(todo ? await api.listItems(todo.id) : [])
      }
    } finally {
      setLoading(false)
    }
  }, [member?.role])

  // Offene, für mich relevante Aufgaben (mir zugewiesen oder offen für alle), noch nicht bestätigt.
  const myOpenTasks = todoItems.filter(
    (i) => i.checkedAt === null && (i.assignedTo === member?.id || i.assignedTo === null),
  )
  async function acceptTask(item: ListItemDto): Promise<void> {
    if (!member) return
    await api.updateItem(item.id, { assignedTo: member.id })
    await load()
  }
  async function reportTaskDone(item: ListItemDto, done: boolean): Promise<void> {
    await api.updateItem(item.id, { reportDone: done })
    await load()
  }
  useEffect(() => {
    void load()
  }, [load])
  useLive('lists', load)
  useLive('points', load)

  async function payout(a: PointsAccountDto): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Auszahlen', message: `${a.memberName}: ${formatPoints(a.balance)} (${pointsToEuro(a.balance)}) ins Taschengeld auszahlen?`, confirmLabel: 'Auszahlen' }))) return
    await api.payoutPoints(a.id)
    await load()
  }
  async function requestPayout(a: PointsAccountDto): Promise<void> {
    await api.requestPayout(a.id)
    await dialogs.alert({ title: 'Anfrage gesendet 💰', message: 'Deine Eltern können jetzt auszahlen.' })
  }
  async function remove(t: TaskTemplateDto): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Vorlage löschen', message: `Vorlage „${t.title}" löschen? Bereits erzeugte Aufgaben in „Zu erledigen" bleiben bestehen.`, confirmLabel: 'Löschen', danger: true }))) return
    await api.deleteTaskTemplate(t.id)
    await load()
  }
  async function togglePause(t: TaskTemplateDto): Promise<void> {
    await api.updateTaskTemplate(t.id, { active: !t.active })
    await load()
  }
  async function instantiate(t: TaskTemplateDto): Promise<void> {
    const created = await api.instantiateTaskTemplate(t.id)
    await load()
    if (created === 0) await dialogs.alert({ title: 'Schon geplant', message: `„${t.title}" steht für heute schon in „Zu erledigen".` })
  }

  if (loading) return <div className="p-6" />

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 p-3 md:p-4">
      {/* Punktekonten */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <h1 className="flex items-center gap-2 text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>
            <Coins className="size-5" style={{ color: 'var(--color-accent)' }} /> {isAdult ? 'Punktekonten' : 'Meine Punkte'}
          </h1>
          {isAdult && children.length > 0 && (
            <Button className="ml-auto" onClick={() => setCreditOpen(true)}><PlusCircle className="size-4" /> Nachtragen</Button>
          )}
        </div>
        {accounts.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Noch keine Punkte — erledige eine Aufgabe und leg los! ⭐</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {accounts.map((a) => (
              <PointsCard key={a.id} a={a} isAdult={!!isAdult} onPayout={() => void payout(a)} onRequest={() => void requestPayout(a)} avatar={members.find((m) => m.id === a.memberId)} />
            ))}
          </ul>
        )}
      </section>

      {/* Kinder: offene Aufgaben (annehmen / fertig melden) */}
      {!isAdult && (
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>
            <ClipboardCheck className="size-5" style={{ color: 'var(--color-accent)' }} /> Meine Aufgaben
          </h2>
          {myOpenTasks.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Gerade nichts zu tun — super! 🎉</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {myOpenTasks.map((item) => {
                const mine = item.assignedTo === member?.id
                const reported = item.reportedDoneAt !== null
                const overdue = !!item.dueAt && new Date(item.dueAt).getTime() < Date.now()
                return (
                  <li key={item.id} className="flex items-center gap-3 rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>{item.text}</span>
                        {item.rewardAmount ? (
                          <span className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-accent)' }}>+{formatPoints(item.rewardAmount)}</span>
                        ) : null}
                      </div>
                      {item.dueAt && (
                        <div className="text-xs" style={{ color: overdue ? 'var(--color-danger)' : 'var(--color-muted)' }}>
                          {overdue ? '⏰ überfällig · bis ' : 'bis '}{new Date(item.dueAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                        </div>
                      )}
                      {reported && <div className="text-xs" style={{ color: '#c48a2b' }}>erledigt gemeldet · wartet auf Bestätigung</div>}
                    </div>
                    {!mine ? (
                      <Button onClick={() => void acceptTask(item)}><Hand className="size-4" /> Annehmen</Button>
                    ) : reported ? (
                      <button onClick={() => void reportTaskDone(item, false)} className="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)', border: '1px solid var(--color-line)' }}>
                        <Clock className="mr-1 inline size-4" />Zurückziehen
                      </button>
                    ) : (
                      <Button onClick={() => void reportTaskDone(item, true)}>Fertig melden!</Button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {/* Aufgaben-Vorlagen (nur Eltern) */}
      {isAdult && (
      <section>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Aufgaben-Vorlagen</h2>
          {isAdult && (
            <Button className="ml-auto" onClick={() => setDialog({ editing: null })}><Plus className="size-4" /> Neue Vorlage</Button>
          )}
        </div>
        {!isAdult && templates.length > 0 && (
          <p className="mb-2 text-xs" style={{ color: 'var(--color-muted)' }}>Annehmen und „Fertig!" melden kannst du sie unter „Zu erledigen".</p>
        )}
        {templates.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            {isAdult ? 'Noch keine Aufgaben-Vorlagen. Lege die erste an — sie erscheint automatisch in „Zu erledigen".' : 'Gerade ist nichts geplant. Neue Aufgaben landen in „Zu erledigen".'}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center gap-3 rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)', opacity: t.active ? 1 : 0.55 }}>
                <div className="flex size-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                  {t.recurrence === 'once' ? <CalendarClock className="size-4" style={{ color: 'var(--color-accent)' }} /> : <Repeat className="size-4" style={{ color: 'var(--color-accent)' }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>{t.title}</span>
                    <span className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-accent)' }}>+{formatPoints(t.points)}</span>
                    {!t.active && <span className="shrink-0 text-xs" style={{ color: 'var(--color-muted)' }}>pausiert</span>}
                  </div>
                  <div className="truncate text-xs" style={{ color: 'var(--color-muted)' }}>{recurrenceText(t)} · {nameOf(t.assigneeMemberId)}</div>
                </div>
                {isAdult && (
                  <div className="flex shrink-0 items-center gap-1">
                    <button onClick={() => void instantiate(t)} aria-label="Sofort in Zu erledigen" title={'Sofort in „Zu erledigen" eintragen'} className="rounded p-1.5 hover:bg-black/5"><PlusCircle className="size-4" style={{ color: 'var(--color-accent)' }} /></button>
                    <button onClick={() => void togglePause(t)} aria-label={t.active ? 'Pausieren' : 'Fortsetzen'} title={t.active ? 'Pausieren' : 'Fortsetzen'} className="rounded p-1.5 hover:bg-black/5">
                      {t.active ? <Pause className="size-4" style={{ color: 'var(--color-muted)' }} /> : <Play className="size-4" style={{ color: 'var(--color-muted)' }} />}
                    </button>
                    <button onClick={() => setDialog({ editing: t })} aria-label="Bearbeiten" className="rounded p-1.5 hover:bg-black/5"><Pencil className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                    <button onClick={() => void remove(t)} aria-label="Löschen" className="rounded p-1.5 hover:bg-black/5"><Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      )}

      {dialog && (
        <TemplateDialog editing={dialog.editing} childMembers={children} onClose={() => setDialog(null)} onSaved={async () => { setDialog(null); await load() }} />
      )}
      {creditOpen && (
        <CreditDialog childMembers={children} onClose={() => setCreditOpen(false)} onSaved={async () => { setCreditOpen(false); await load() }} />
      )}
    </div>
  )
}

/**
 * „Aufgabe nachtragen" (ADR-077): Eltern schreiben einem Kind eine bereits
 * erledigte Aufgabe direkt gut — Punkte + Feed-Eintrag sofort, ohne den
 * Melde-/Bestätigen-Umweg. Nützlich, wenn ein Kind die Arbeit schon getan hat.
 */
function CreditDialog({ childMembers, onClose, onSaved }: { childMembers: MemberDto[]; onClose: () => void; onSaved: () => Promise<void> }): React.JSX.Element {
  const [childId, setChildId] = useState(childMembers[0]?.id ?? '')
  const [title, setTitle] = useState('')
  const [points, setPoints] = useState('50')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(): Promise<void> {
    const p = Number(points)
    if (!childId) { setError('Bitte ein Kind wählen.'); return }
    if (!title.trim()) { setError('Bitte einen Titel angeben.'); return }
    if (!Number.isInteger(p) || p < 0) { setError('Bitte ganze Punkte angeben.'); return }
    setBusy(true)
    setError(null)
    try {
      await api.creditTask({ childId, title: title.trim(), points: p })
      await onSaved()
    } catch {
      setError('Gutschreiben fehlgeschlagen. Bitte Eingaben prüfen.')
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Aufgabe nachtragen</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Für wen?</span>
            <select className={field} style={fieldStyle} value={childId} onChange={(e) => setChildId(e.target.value)}>
              {childMembers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <input className={field} style={fieldStyle} placeholder="Aufgabe (z. B. Müll runterbringen)" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Punkte (100 P = 1 €)</span>
            <input className={field} style={fieldStyle} inputMode="numeric" value={points} onChange={(e) => setPoints(e.target.value)} placeholder="z. B. 50" />
          </label>
          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
            Wird sofort gutgeschrieben: die Punkte landen direkt auf dem Konto und im Feed erscheint „⭐ … erledigt". Kein Melden/Bestätigen nötig.
          </p>
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={save} disabled={busy || !title.trim() || !childId}>Gutschreiben</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function PointsCard({ a, isAdult, onPayout, onRequest, avatar }: { a: PointsAccountDto; isAdult: boolean; onPayout: () => void; onRequest: () => void; avatar?: MemberDto }): React.JSX.Element {
  const pct = Math.min(100, Math.round((a.balance / a.threshold) * 100))
  const remaining = Math.max(0, a.threshold - a.balance)
  return (
    <li className="rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
      <div className="mb-2 flex items-center gap-2">
        {avatar && <Avatar name={avatar.name} color={avatar.color} size={28} src={avatar.avatarUrl} />}
        <span className="font-medium" style={{ color: 'var(--color-ink)' }}>{a.memberName}</span>
        <span className="ml-auto text-sm font-semibold" style={{ color: 'var(--color-accent)' }}>{formatPoints(a.balance)} <span className="font-normal" style={{ color: 'var(--color-muted)' }}>= {pointsToEuro(a.balance)}</span></span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-surface)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: a.canPayout ? 'var(--color-success)' : 'var(--color-accent)' }} />
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {a.canPayout ? `Auszahlung möglich! 💰` : `${a.balance} / ${a.threshold} P · noch ${remaining} P bis zur Auszahlung`}
        </span>
        {a.canPayout && (
          <Button className="ml-auto" onClick={isAdult ? onPayout : onRequest}>{isAdult ? 'Auszahlen' : 'Auszahlung anfragen'}</Button>
        )}
      </div>
    </li>
  )
}

function TemplateDialog({ editing, childMembers, onClose, onSaved }: { editing: TaskTemplateDto | null; childMembers: MemberDto[]; onClose: () => void; onSaved: () => Promise<void> }): React.JSX.Element {
  const [title, setTitle] = useState(editing?.title ?? '')
  const [description, setDescription] = useState(editing?.description ?? '')
  const [points, setPoints] = useState(editing ? String(editing.points) : '50')
  const [recurrence, setRecurrence] = useState<TaskRecurrence>(editing?.recurrence ?? 'once')
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(editing?.daysOfWeek ?? [1])
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>(editing?.daysOfMonth ?? [1])
  const [dueAt, setDueAt] = useState(editing?.dueAt ? toLocalDate(editing.dueAt) : '')
  // Zuweisung: '' = offen für alle, 'perchild' = jedes Kind für sich, sonst memberId
  const [assign, setAssign] = useState<string>(editing ? (editing.perChild ? 'perchild' : (editing.assigneeMemberId ?? '')) : '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // WiederholungsART ist nach dem Anlegen fix; die Tage lassen sich aber ändern.
  const recurLocked = editing !== null
  const toggle = (arr: number[], v: number): number[] => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v])

  async function save(): Promise<void> {
    const p = Number(points)
    if (!title.trim() || !Number.isInteger(p) || p < 0) { setError('Bitte Titel und ganze Punkte angeben.'); return }
    if (recurrence === 'once' && !dueAt) { setError('Bitte ein Fälligkeitsdatum („zu erledigen bis") wählen.'); return }
    if (recurrence === 'weekly' && daysOfWeek.length === 0) { setError('Bitte mindestens einen Wochentag wählen.'); return }
    if (recurrence === 'monthly' && daysOfMonth.length === 0) { setError('Bitte mindestens einen Tag im Monat wählen.'); return }
    setBusy(true)
    setError(null)
    try {
      const assigneeMemberId = assign && assign !== 'perchild' ? assign : null
      const perChild = assign === 'perchild'
      // Aufgaben ohne Uhrzeit (ADR-070): „einmalig" = gewähltes Datum, fällig zum
      // Tagesende (23:59 lokal); wiederkehrend hat gar kein Fälligkeits-Timing mehr.
      const timing = recurrence === 'once' ? { dueAt: new Date(`${dueAt}T23:59:59`).toISOString() } : {}
      const days = recurrence === 'weekly' ? { daysOfWeek } : recurrence === 'monthly' ? { daysOfMonth } : {}
      if (editing) {
        await api.updateTaskTemplate(editing.id, { title: title.trim(), description: description.trim() || null, points: p, assigneeMemberId, perChild, ...days, ...timing })
      } else {
        await api.createTaskTemplate({ title: title.trim(), description: description.trim() || null, points: p, recurrence, assigneeMemberId, perChild, ...days, ...timing })
      }
      await onSaved()
    } catch {
      setError('Speichern fehlgeschlagen. Bitte Eingaben prüfen.')
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>{editing ? 'Vorlage bearbeiten' : 'Neue Aufgaben-Vorlage'}</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="flex flex-col gap-3">
          <input className={field} style={fieldStyle} placeholder="Aufgabe (z. B. Spülmaschine ausräumen)" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          <input className={field} style={fieldStyle} placeholder="Beschreibung (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Punkte (100 P = 1 €)</span>
            <input className={field} style={fieldStyle} inputMode="numeric" value={points} onChange={(e) => setPoints(e.target.value)} placeholder="z. B. 50" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Wiederholung</span>
            <select className={field} style={fieldStyle} value={recurrence} onChange={(e) => setRecurrence(e.target.value as TaskRecurrence)} disabled={recurLocked}>
              {TASK_RECURRENCES.map((r) => <option key={r} value={r}>{RECUR_LABEL[r]}</option>)}
            </select>
          </label>

          {recurrence === 'weekly' && (
            <div className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>An diesen Wochentagen (mehrere möglich)</span>
              <div className="flex flex-wrap gap-1.5">
                {[1, 2, 3, 4, 5, 6, 0].map((i) => (
                  <button key={i} type="button" onClick={() => setDaysOfWeek((a) => toggle(a, i))} className="rounded-full px-3 py-1 text-sm" style={daysOfWeek.includes(i) ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)', border: '1px solid var(--color-line)' }}>{WD[i]}</button>
                ))}
              </div>
            </div>
          )}
          {recurrence === 'monthly' && (
            <div className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>An diesen Tagen im Monat (mehrere möglich)</span>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 31 }, (_, k) => k + 1).map((d) => (
                  <button key={d} type="button" onClick={() => setDaysOfMonth((a) => toggle(a, d))} className="size-8 rounded-md text-xs" style={daysOfMonth.includes(d) ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)', border: '1px solid var(--color-line)' }}>{d}</button>
                ))}
              </div>
            </div>
          )}
          {recurrence === 'once' && (
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Zu erledigen bis</span>
              <input type="date" className={field} style={fieldStyle} value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </label>
          )}

          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Wer?</span>
            <select className={field} style={fieldStyle} value={assign} onChange={(e) => setAssign(e.target.value)}>
              <option value="">Offen für alle Kinder</option>
              <option value="perchild">Jedes Kind für sich</option>
              {childMembers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {assign === 'perchild'
              ? 'Jedes Kind bekommt diese Aufgabe als eigene Aufgabe (z. B. „Zimmer aufräumen") — anders als bei „Offen für alle", wo nur eines sie übernimmt.'
              : 'Die Aufgabe erscheint automatisch in „Zu erledigen". „Offen" heißt: das erste Kind, das annimmt, bekommt die Punkte nach eurer Bestätigung.'}
          </p>
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={save} disabled={busy || !title.trim()}>{editing ? 'Speichern' : 'Anlegen'}</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** ISO → Wert für <input type="date"> (lokales Datum, ohne Uhrzeit). */
function toLocalDate(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
